import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Stack, Tab, Tabs, type Theme, useMediaQuery } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Spinner from '/@/components/spinner';
import useUnitProvider from '/@/core/context/unit';
import settings from '/@/core/settings';
import { _ } from '/@/locale';
import { getCropConfig } from '/@/store/private-data/recommendations/selectors';
import { updateActiveRecommendationCropConfig } from '/@/store/recommendations/actions';
import { getActiveRecommendation } from '/@/store/recommendations/selectors';
import type { SeedRecommendationManagementTab } from '/@/store/tracking/events/seed-recommendation';
import type { Field } from '/@/types/field';
import type { Recommendation } from '/@/types/recommendation';
import { useCopyBlockImage } from '/@/views/recommendations/v2/result/use-copy-block-image';
import Section from '/@/views/recommendations/v2/section';
import { useHybrids } from '../hybrids/use-hybrids';
import AddHybridModal from './add-hybrid-modal';
import CardCarousel from './card-carousel';
import DensityCaption from './density-caption';
import DensityCurve from './density-curve';
import DensitySimulation from './density-simulation';
import { DEFAULT_ENVIRONMENT_VARIABILITY } from './density-simulation-data';
import FertilizationTab from './fertilization-tab';
import HybridDensityCard from './hybrid-density-card';
import NumberModal from './number-modal';
import PhenologyTab from './phenology-tab';
import { useManagementData } from './use-management-data';

/**
 * "Manejo recomendado" — the wizard's last step: density, phenology and fertilization for the
 * hybrids chosen on the previous step. The recommendation itself is saved by the view that owns
 * the wizard, on Terminar.
 */
function ManagementStep({
    field,
    crop = 'corn',
    onEditEnvironment,
    maxHybrids = settings.maxProductSelection,
    recommendationForCopy,
    onTabViewed,
}: {
    field: Field;
    crop?: 'corn' | 'soy';
    /** Applying a new planting date invalidates the environment, so the wizard steps back. */
    onEditEnvironment: () => void;
    /**
     * How many hybrids this recommendation may hold. The management recommendation carries
     * exactly one, so its step shows no empty slots and no way to add a second.
     */
    maxHybrids?: number;
    /**
     * The draft recommendation the wizard autosaves on reaching this step, in the shape the
     * block-image endpoint expects. Undefined for the brief window before that autosave resolves
     * — the copy buttons stay hidden until then, same as the result page's loading window.
     */
    recommendationForCopy?: Recommendation;
    /** Reports each tab as it is shown, the one open by default included. */
    onTabViewed?: (tab: SeedRecommendationManagementTab) => void;
}) {
    const dispatch = useDispatch();
    const units = useUnitProvider();
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
    const recommendation = useSelector(getActiveRecommendation);
    const cropConfig = getCropConfig(recommendation, crop);
    const { copyBlock, copyingBlock } = useCopyBlockImage(recommendationForCopy, 'management');

    const [tab, setTab] = useState<SeedRecommendationManagementTab>('density');
    const tabsRef = useRef<HTMLDivElement>(null);

    /**
     * A kept planting date lands back on Densidad. The button was pressed deep in Fenología, so
     * the tab bar is brought back to the top rather than leaving the user mid-way down density.
     */
    const showDensityAfterDateKept = () => {
        setTab('density');
        tabsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    const [achievementOpen, setAchievementOpen] = useState(false);
    const [areaOpen, setAreaOpen] = useState(false);
    const [addHybridOpen, setAddHybridOpen] = useState(false);

    // biome-ignore lint/correctness/useExhaustiveDependencies: reports tab changes, not callback identity
    useEffect(() => {
        onTabViewed?.(tab);
    }, [tab]);

    const { hybrids, planterEfficiency, areaInHectares, isLoading, isLoadingFertilization } = useManagementData({
        field,
        crop,
    });
    const { featuredProducts, otherProducts } = useHybrids({ field, crop });

    /** The area in the user's own unit, for the caption and the edit modal. */
    const area = areaInHectares !== undefined ? units.toArea(areaInHectares, 2) : undefined;

    const selectedIds: number[] = cropConfig?.products || [];
    const emptySlots = Math.max(0, maxHybrids - selectedIds.length);
    const candidates = [...featuredProducts, ...otherProducts].filter((hybrid) => !selectedIds.includes(hybrid.id));

    const updateCropConfig = (key: string, value: unknown) =>
        dispatch(updateActiveRecommendationCropConfig({ key, value }));

    if (!cropConfig) {
        return (
            <Stack alignItems='center' py={6}>
                <Spinner />
            </Stack>
        );
    }

    const cards = [
        ...hybrids.map((hybrid) => (
            <HybridDensityCard
                key={hybrid.id}
                hybrid={hybrid}
                planterEfficiency={planterEfficiency}
                areaInHectares={areaInHectares}
            />
        )),
        ...Array.from({ length: emptySlots }).map((_slot, index) => (
            <Stack
                // biome-ignore lint/suspicious/noArrayIndexKey: placeholders have no identity
                key={`empty-${index}`}
                alignItems='center'
                justifyContent='center'
                sx={{
                    minHeight: 160,
                    height: '100%',
                    borderRadius: '4px',
                    border: '1px dashed',
                    borderColor: (theme) => theme.palette.divider,
                }}
            >
                <Button
                    variant='text'
                    color='primary'
                    startIcon={<AddIcon />}
                    onClick={() => setAddHybridOpen(true)}
                    disabled={candidates.length === 0}
                >
                    {_('Agregar un híbrido')}
                </Button>
            </Stack>
        )),
    ];

    // Counts the empty "add a hybrid" slots too: a hybrid recommendation with one hybrid picked
    // out of three still has a row of cards, and the curve keeps the full width above it.
    const singleCardRow = cards.length === 1 && !isMobile;

    return (
        <Stack spacing={3} pb={4}>
            <Tabs
                ref={tabsRef}
                value={tab}
                onChange={(_event, value) => setTab(value)}
                variant='fullWidth'
                textColor='secondary'
                indicatorColor='secondary'
            >
                <Tab value='density' label={_('Densidad')} />
                <Tab value='phenology' label={_('Fenología')} />
                <Tab value='fertilization' label={_('Fertilización')} />
            </Tabs>

            {tab === 'phenology' ? (
                <PhenologyTab
                    field={field}
                    crop={crop}
                    plantingDate={cropConfig?.plantingDate}
                    compact={isMobile}
                    onPlantingDateApplied={onEditEnvironment}
                    onPlantingDateKept={showDensityAfterDateKept}
                    onCopy={recommendationForCopy ? () => copyBlock('phenology') : undefined}
                    copying={copyingBlock === 'phenology'}
                />
            ) : tab === 'fertilization' ? (
                <FertilizationTab
                    hybrids={hybrids}
                    isLoading={isLoadingFertilization}
                    onCopy={recommendationForCopy ? () => copyBlock('nutrients') : undefined}
                    copying={copyingBlock === 'nutrients'}
                />
            ) : (
                <Stack spacing={3}>
                    <Section
                        title={_('Densidad objetivo')}
                        subtitle={_(
                            'Cantidad mínima de plantas a cosecha para el máximo rendimiento en el ambiente definido.'
                        )}
                        block={recommendationForCopy ? 'density_curve' : undefined}
                        onCopy={recommendationForCopy ? () => copyBlock('density_curve') : undefined}
                        copying={copyingBlock === 'density_curve'}
                    >
                        {isLoading && !hybrids.length ? (
                            <Stack alignItems='center' py={6}>
                                <Spinner />
                            </Stack>
                        ) : singleCardRow ? (
                            // One card and no empty slot: it sits beside the curve rather than
                            // under it, which is the whole point of having only one hybrid —
                            // there is no row of cards to compare across.
                            <Box
                                display='grid'
                                gap={2}
                                gridTemplateColumns='minmax(0, 1fr) minmax(260px, 320px)'
                                alignItems='start'
                            >
                                <DensityCurve hybrids={hybrids} height={320} />
                                {cards}
                            </Box>
                        ) : (
                            <DensityCurve hybrids={hybrids} height={isMobile ? 220 : 320} />
                        )}
                    </Section>

                    {/* Too wide to stack on a phone. */}
                    {singleCardRow ? null : isMobile ? (
                        <CardCarousel>{cards}</CardCarousel>
                    ) : (
                        <Box
                            display='grid'
                            gap={2}
                            gridTemplateColumns={{ sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }}
                            alignItems='stretch'
                        >
                            {cards}
                        </Box>
                    )}

                    <DensityCaption
                        planterEfficiency={planterEfficiency}
                        areaInHectares={areaInHectares}
                        actions={[
                            {
                                label: _('Cambiar porcentaje'),
                                onClick: () => setAchievementOpen(true),
                                kind: 'link',
                            },
                            {
                                label: _('Cambiar superficie'),
                                onClick: () => setAreaOpen(true),
                                kind: 'link',
                            },
                        ]}
                    />
                    {!isLoading && (
                        <DensitySimulation
                            hybrids={hybrids}
                            environment={cropConfig.environment}
                            userDensity={cropConfig.userDensity}
                            variability={cropConfig.userBetterEnvironment ?? DEFAULT_ENVIRONMENT_VARIABILITY}
                            onChange={({ userDensity, variability }) => {
                                if (userDensity !== undefined) updateCropConfig('userDensity', userDensity);
                                // One slider, two columns: the shift is symmetric in this flow.
                                if (variability !== undefined) {
                                    updateCropConfig('userBetterEnvironment', variability);
                                    updateCropConfig('userWorseEnvironment', variability);
                                }
                            }}
                            onCopy={
                                recommendationForCopy
                                    ? (copy) => copyBlock('density_simulation', { densitySimulation: copy })
                                    : undefined
                            }
                            copying={copyingBlock === 'density_simulation'}
                        />
                    )}
                </Stack>
            )}

            <NumberModal
                open={achievementOpen}
                onClose={() => setAchievementOpen(false)}
                onApply={(value) => {
                    updateCropConfig('userPlanterEfficiency', value);
                    setAchievementOpen(false);
                }}
                title={_('Cambiar el porcentaje de logro')}
                description={_('Ingresá el porcentaje de plantas que suelen nacer con éxito en el lote.')}
                value={planterEfficiency}
                suffix='%'
                min={1}
                max={100}
                rangeError={_('El porcentaje debe ser de 1 a 100%')}
            />

            <NumberModal
                open={areaOpen}
                onClose={() => setAreaOpen(false)}
                onApply={(value) => {
                    // `userArea` is stored in hectares, the same as the legacy calculator stores
                    // it — the input is in whatever area unit the user prefers.
                    updateCropConfig('userArea', units.fromArea(value));
                    setAreaOpen(false);
                }}
                title={_('Cambiar la superficie del lote')}
                description={_('Ingresá la superficie a sembrar para calcular las bolsas totales.')}
                value={area?.value}
                suffix={units.getUnit('area', crop)}
                min={units.toArea(1, 2).value}
                rangeError={_('Ingresá una superficie válida.')}
            />

            {emptySlots > 0 && (
                <AddHybridModal
                    open={addHybridOpen}
                    onClose={() => setAddHybridOpen(false)}
                    onAdd={(id) => updateCropConfig('products', [...selectedIds, id])}
                    candidates={candidates}
                />
            )}
        </Stack>
    );
}

export default ManagementStep;
